import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Offcanvas } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faPaperPlane, faXmark } from '@fortawesome/free-solid-svg-icons';
import { User } from '../types';
import { API_BASE_URL } from '../config/api';

interface ChatMessage {
  fingerprint?: number | string;
  type: string;
  from: 'user' | 'operator' | string;
  content: string;
  fileUrl?: string | null;
  fileName?: string | null;
  mimeType?: string | null;
  timestamp?: number;
  nickname?: string | null;
}

const isImageAttachment = (message: ChatMessage) => {
  const mime = String(message.mimeType || '').toLowerCase();
  if (mime.startsWith('image/') && !mime.includes('heic') && !mime.includes('heif')) {
    return Boolean(message.fileUrl);
  }
  const name = String(message.fileName || message.content || '').toLowerCase();
  return Boolean(message.fileUrl) && /\.(png|jpe?g|gif|webp|bmp)$/i.test(name);
};

interface UserChatDrawerProps {
  user: User | null;
  onClose: () => void;
  onMarkedRead?: () => void;
}

const POLL_MS = 4000;

const formatMessageTime = (timestamp?: number) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  return sameDay
    ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const UserChatDrawer: React.FC<UserChatDrawerProps> = ({ user, onClose, onMarkedRead }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const onMarkedReadRef = useRef(onMarkedRead);
  const markedReadUserIdRef = useRef<string | null>(null);
  const userId = user?._id || null;
  onMarkedReadRef.current = onMarkedRead;

  const scrollToBottom = useCallback(() => {
    const node = listRef.current;
    if (node) {
      node.scrollTop = node.scrollHeight;
    }
  }, []);

  const loadConversation = useCallback(async (silent = false) => {
    if (!userId) return;
    if (!silent) {
      setLoading(true);
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/chat/users/${userId}`, {
        withCredentials: true,
      });
      setMessages(response.data.messages || []);
      setSessionId(response.data.sessionId || null);
      setError(null);
      if (!silent && response.data.sessionId && markedReadUserIdRef.current !== userId) {
        try {
          await axios.post(
            `${API_BASE_URL}/chat/users/${userId}/read`,
            {},
            { withCredentials: true }
          );
          markedReadUserIdRef.current = userId;
          onMarkedReadRef.current?.();
        } catch (_err) {
          // Keep the thread visible even if Crisp rejects the read receipt.
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load chat');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      markedReadUserIdRef.current = null;
      setMessages([]);
      setSessionId(null);
      setError(null);
      setDraft('');
      setPreviewImage(null);
      return;
    }
    loadConversation();
    const timer = window.setInterval(() => loadConversation(true), POLL_MS);
    return () => window.clearInterval(timer);
  }, [loadConversation, userId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (userId && sessionId) {
      inputRef.current?.focus();
    }
  }, [sessionId, userId]);

  const sendMessage = async () => {
    const content = draft.trim();
    if (!content || !userId || sending || !sessionId) {
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/chat/users/${userId}/messages`,
        { content },
        { withCredentials: true }
      );
      setMessages(response.data.messages || []);
      setSessionId(response.data.sessionId || null);
      setDraft('');
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send message');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    sendMessage();
  };

  const handleComposerKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const closePreview = useCallback(() => {
    setPreviewImage(null);
  }, []);

  useEffect(() => {
    if (!previewImage) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closePreview();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [closePreview, previewImage]);

  const displayName = user ? `${user.firstName} ${user.lastName}`.trim() : '';

  return (
    <>
    <Offcanvas
      show={Boolean(user)}
      onHide={onClose}
      placement="end"
      keyboard={!previewImage}
      className="user-chat-drawer"
    >
      <Offcanvas.Header closeButton className="user-chat-drawer-header">
        <Offcanvas.Title>
          <div className="d-flex align-items-center gap-2">
            <span className="user-chat-drawer-icon">
              <FontAwesomeIcon icon={faComments} />
            </span>
            <span className="text-truncate">{displayName || 'Chat'}</span>
          </div>
        </Offcanvas.Title>
      </Offcanvas.Header>
      {user && (
        <Offcanvas.Body className="d-flex flex-column p-0">
          <div className="user-chat-drawer-meta">
            <div className="text-truncate" title={user.email}>{user.email}</div>
            <div className="d-flex align-items-center gap-2 mt-1">
              {user.role && <span className="badge bg-secondary">{user.role}</span>}
              <span className={`user-chat-status ${sessionId ? 'is-linked' : 'is-waiting'}`}>
                {sessionId ? 'Session linked' : 'Waiting for app chat'}
              </span>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger rounded-0 mb-0 px-3 py-2 small" role="alert">
              {error}
            </div>
          )}

          <div ref={listRef} className="user-chat-drawer-messages">
            {loading ? (
              <div className="d-flex justify-content-center py-5">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : messages.length > 0 ? (
              messages.map((message, index) => {
                const fromUser = message.from === 'user';
                return (
                  <div
                    key={String(message.fingerprint ?? `${message.timestamp}-${index}`)}
                    className={`user-chat-bubble ${fromUser ? 'from-user' : 'from-operator'}`}
                  >
                    <div className="user-chat-bubble-meta">
                      {fromUser ? displayName || 'User' : message.nickname || 'Fluora'}
                      {message.timestamp ? ` · ${formatMessageTime(message.timestamp)}` : ''}
                    </div>
                    <div className="user-chat-bubble-body">
                      {message.type === 'file' && message.fileUrl ? (
                        isImageAttachment(message) ? (
                          <button
                            type="button"
                            className="user-chat-attachment-link"
                            onClick={() => setPreviewImage({
                              url: message.fileUrl as string,
                              name: message.fileName || message.content || 'Image',
                            })}
                          >
                            <img
                              src={message.fileUrl}
                              alt={message.fileName || message.content || 'Image'}
                              className="user-chat-attachment-image"
                            />
                          </button>
                        ) : (
                          <a
                            href={message.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="user-chat-attachment-file"
                          >
                            {message.fileName || message.content || 'Attachment'}
                          </a>
                        )
                      ) : (
                        message.content
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="user-chat-empty">
                <FontAwesomeIcon icon={faComments} className="mb-2" />
                <div>
                  {sessionId
                    ? 'No messages in this thread yet. Send the first reply below.'
                    : 'This user has not opened chat in the Fluora app yet. The thread will appear here after they sign in and tap Chat.'}
                </div>
              </div>
            )}
          </div>

          <form className="user-chat-drawer-composer" onSubmit={handleSubmit}>
            <textarea
              ref={inputRef}
              className="form-control"
              rows={2}
              placeholder={sessionId ? 'Write a reply…' : 'Waiting for the user to start chat'}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleComposerKeyDown}
              disabled={!sessionId || sending}
            />
            <div className="d-flex justify-content-between align-items-center mt-2">
              <span className="small text-muted">
                {sessionId ? 'Enter to send · Shift+Enter for a new line' : 'Chat starts from the mobile app'}
              </span>
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={!sessionId || sending || !draft.trim()}
              >
                {sending ? (
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                ) : (
                  <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
                )}
                Send
              </button>
            </div>
          </form>
        </Offcanvas.Body>
      )}
    </Offcanvas>
      {previewImage
        ? createPortal(
            <div
              className="user-chat-lightbox"
              onClick={closePreview}
              role="dialog"
              aria-modal="true"
              aria-label={previewImage.name}
            >
              <button
                type="button"
                className="user-chat-lightbox-close"
                onClick={closePreview}
                aria-label="Close image"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
              <img
                src={previewImage.url}
                alt={previewImage.name}
                className="user-chat-lightbox-image"
                onClick={(event) => event.stopPropagation()}
              />
            </div>,
            document.body
          )
        : null}
    </>
  );
};

export default UserChatDrawer;
